import React, { useEffect, useState } from "react"
import PubSub from "pubsub-js"
import Button from "aldoo-ra/Button"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { showNotification } from "aldoo-ra/Notification"
import { VariantReader } from "aldoo-ra/VariantReader"
import { twMerge } from "tailwind-merge"
import { ArrowDownTrayIcon, LockClosedIcon } from "@heroicons/react/24/outline"
import { GetDownloadable, GetDownloadLink, GetChangelog } from "./api"
import { changelogLines } from "./changelog-lines"

const formatSize = (bytes) => {
  if (!bytes) return ""
  const units = ["B", "KB", "MB", "GB"]
  let value = bytes, unit = 0
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit++ }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`
}

/**
 * Downloadable widget — drop anywhere with the downloadable's id (did),
 * mirroring the AldooAds unit pattern:
 *
 *   <Downloadable id="snap-macos" />
 *
 * Shows name, latest version and size; the button authorizes via get_link
 * and navigates to the short-lived signed URL. Ineligible users see the
 * reason (sign in / license required). Renders nothing while loading or
 * when the id is unknown.
 */
export default function Downloadable({
  id,
  className = "",
  onDownloaded,
  showChangelog = true,
  changelogLimit = 5,
  variant = "default",
  styleOverrides,
}) {
  const texts = TextBundle({ id: "downloadables" })
  const [data, setData] = useState(null)
  const [changelog, setChangelog] = useState(null)
  const [downloading, setDownloading] = useState(false)

  const variants = VariantReader.useVariant({
    componentName: "Downloadables",
    variant,
    styleOverrides,
  })

  const {
    container,
    card,
    icon_wrapper,
    icon,
    info,
    title: titleClass,
    description: descriptionClass,
    meta,
    meta_version,
    action,
    button_variant,
    action_link,
    changelog_container,
    changelog_heading,
    changelog_entries,
    changelog_version,
    changelog_date,
    changelog_text,
    changelog_line,
    changelog_bullet,
  } = variants

  useEffect(() => {
    if (!id) return
    let active = true
    GetDownloadable(id)
      .then((res) => active && res?.result && setData(res))
      .catch(() => { })
    return () => { active = false }
  }, [id])

  // Changelog is gated by the same eligibility as the download — only
  // fetch once we know the caller qualifies
  useEffect(() => {
    if (!showChangelog || !data?.eligible || !data?.downloadable?.did) return
    let active = true
    GetChangelog(data.downloadable.did, { limit: changelogLimit })
      .then((res) => active && res?.result && setChangelog(res.changelog))
      .catch(() => { })
    return () => { active = false }
  }, [showChangelog, data, changelogLimit])

  if (!data?.downloadable?.latest) return null

  const { downloadable, eligible, reason } = data
  const { latest } = downloadable

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const res = await GetDownloadLink(downloadable.did)
      if (res?.error || !res?.url) {
        showNotification({
          message: res?.reason === "authentication_required"
            ? (texts?.signin_required || "Sign in to download this file.")
            : res?.reason
              ? (texts?.license_required || "A valid license is required to download this file.")
              : (texts?.download_error || "Could not start the download."),
          className: "bg-error text-white",
        })
        return
      }
      window.location.href = res.url
      onDownloaded?.(res)
    } finally {
      setDownloading(false)
    }
  }

  const changelogEntries = (changelog || []).filter((e) => e.changelog)

  return (
    <div className={twMerge(container, className)}>
    <div className={card}>
      {/* Leading icon */}
      <div className={icon_wrapper}>
        <ArrowDownTrayIcon className={icon} />
      </div>

      {/* Title + meta, stacked */}
      <div className={info}>
        <div className={titleClass}>
          {downloadable.name}
        </div>

        {downloadable.description && (
          <div className={descriptionClass}>
            {downloadable.description}
          </div>
        )}

        <div className={meta}>
          <span className={meta_version}>
            {texts?.version_label || "Version"} {latest.version}
          </span>
          {latest.size ? ` · ${formatSize(latest.size)}` : ""}
          {downloadable.supported_platforms?.length
            ? ` · ${downloadable.supported_platforms.join(", ")}`
            : ""}
        </div>
      </div>

      <div className={action}>
        {eligible ? (
          <Button variant={button_variant || "primary"} onClick={handleDownload} disabled={downloading}>
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            {downloading ? (texts?.preparing || "Preparing...") : (texts?.download || "Download")}
          </Button>
        ) : (
          // Not eligible → route the user to the fix: sign in (dialog) or
          // activate/purchase a license
          reason === "authentication_required" ? (
            <button
              onClick={() => PubSub.publish("signin", {})}
              className={action_link}
            >
              <LockClosedIcon className="w-4 h-4" />
              {texts?.signin_required || "Sign in to download"}
            </button>
          ) : (
            <a
              href="/account/licenses"
              className={action_link}
            >
              <LockClosedIcon className="w-4 h-4" />
              {texts?.license_required || "License required — activate one"}
            </a>
          )
        )}
      </div>
    </div>

    {/* App-store-style changelog: version caps-bold left, date right,
        release notes on the lines below */}
    {changelogEntries.length > 0 && (
      <div className={changelog_container}>
        <div className={changelog_heading}>
          {texts?.whats_new || "What's new"}
        </div>
        <div className={changelog_entries}>
          {changelogEntries.map((entry) => (
            <div key={entry.version}>
              <div className="flex items-baseline gap-3">
                <span className={changelog_version}>
                  {texts?.version_label || "Version"} {entry.version}
                </span>
                {entry.created && (
                  <span className={changelog_date}>
                    {new Date(entry.created).toLocaleDateString()}
                  </span>
                )}
              </div>
              <ul className={changelog_text}>
                {changelogLines(entry.changelog).map((line, i) => (
                  <li key={i} className={changelog_line}>
                    <span className={changelog_bullet} aria-hidden="true">–</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    )}
    </div>
  )
}
